// Client Work Planner — a spreadsheet-style monthly work planner over the CRM's
// existing task system. Pick a client, see one row per day of the month with two
// task slots (Work / Assign / Status), inline edits, drag-to-reschedule, Sunday
// rows, +N-more overflow, summary, filters, month nav, and a PDF report. List
// and Calendar remain as alternate views. Tasks are real CRM `tasks` rows scoped
// to the client, so changes here and elsewhere in the CRM stay in sync.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { startOfMonth, endOfMonth, subMonths, format } from "date-fns";
import html2pdf from "html2pdf.js";
import {
  Search, X, LayoutGrid, List, CalendarDays, Building2, ChevronLeft, ChevronRight,
  FileDown, Loader2, Plus,
} from "lucide-react";
import { workPlannerApi, PLANNER_STATUSES, PLANNER_PRIORITIES, plannerStatusMeta, normalizePlannerStatus } from "@/services/workPlannerApi";
import PlannerGrid from "./PlannerGrid";
import PlannerTaskDialog from "./PlannerTaskDialog";
import TasksCalendarView from "@/components/tasks/TasksCalendarView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const toDate = (v) => { const d = v ? new Date(v) : null; return d && !isNaN(d.getTime()) ? d : null; };
const taskDate = (t) => toDate(t.due_date || t.scheduled_date || t.start_date);
const memberName = (members, id) => (members.find((m) => m.user_id === id) || {}).name || "—";
// Calendar view expects PRIORITY_CONFIG-shaped dotColor lookups.
const PRIORITY_CONFIG = Object.fromEntries(PLANNER_PRIORITIES.map((p) => [p.value, { label: p.label, dotColor: p.dot }]));

export default function ClientWiseView() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const year = Number(searchParams.get("year")) || new Date().getFullYear();
  const month = Number(searchParams.get("month")) || new Date().getMonth() + 1;

  const [allClients, setAllClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(!clientId);
  const [selectedClient, setSelectedClient] = useState(null);
  const [allTasks, setAllTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [viewMode, setViewMode] = useState("planner"); // planner | list | calendar
  const [range, setRange] = useState(null); // { start, end, label } — list-only multi-month
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [defaultDate, setDefaultDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const pdfRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/clients`, { credentials: "include" })
      .then((r) => r.json()).then((d) => setAllClients(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BACKEND_URL}/api/team/members`, { credentials: "include" })
      .then((r) => r.json()).then((d) => setMembers(Array.isArray(d) ? d.filter((m) => m.status === "approved") : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!clientId) { setSelectedClient(null); setAllTasks([]); return; }
    setLoading(true);
    Promise.all([
      fetch(`${BACKEND_URL}/api/clients/${clientId}`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
      fetch(`${BACKEND_URL}/api/clients/${clientId}/tasks`, { credentials: "include" }).then((r) => (r.ok ? r.json() : [])),
    ])
      .then(([client, tasks]) => { setSelectedClient(client); setAllTasks(Array.isArray(tasks) ? tasks : []); setPickerOpen(false); })
      .catch(() => toast.error("Failed to load client work"))
      .finally(() => setLoading(false));
  }, [clientId]);

  const refetch = () => {
    if (!clientId) return;
    fetch(`${BACKEND_URL}/api/clients/${clientId}/tasks`, { credentials: "include" })
      .then((r) => r.json()).then((d) => setAllTasks(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const filteredClientOptions = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return allClients;
    return allClients.filter((c) => (c.client_name || c.name || "").toLowerCase().includes(q) || (c.company_name || "").toLowerCase().includes(q));
  }, [allClients, clientSearch]);

  const setMonth = (y, m) => setSearchParams({ year: y, month: m });
  const changeMonth = (delta) => { let m = month + delta, y = year; if (m > 12) { m = 1; y++; } else if (m < 1) { m = 12; y--; } setRange(null); setMonth(y, m); };
  const goToday = () => { const n = new Date(); setRange(null); setMonth(n.getFullYear(), n.getMonth() + 1); };
  const setQuickRange = (months, label) => {
    const end = new Date();
    const start = startOfMonth(subMonths(end, months - 1));
    setRange({ start: start.toISOString(), end: end.toISOString(), label });
    setViewMode("list");
  };
  const clearRange = () => setRange(null);

  const applyFilters = (list) => list.filter((t) => {
    if (search && !(t.title || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && normalizePlannerStatus(t.status) !== statusFilter) return false;
    if (priorityFilter !== "all" && (t.priority || "medium") !== priorityFilter) return false;
    if (assigneeFilter !== "all" && !(t.assigned_users || []).includes(assigneeFilter)) return false;
    return true;
  });

  // Planner/Calendar operate on the selected month; List honours an active range.
  const monthTasks = useMemo(() => {
    const start = startOfMonth(new Date(year, month - 1, 1)), end = endOfMonth(start);
    return applyFilters(allTasks.filter((t) => { const d = taskDate(t); return d && d >= start && d <= end; }));
  }, [allTasks, year, month, search, statusFilter, priorityFilter, assigneeFilter]);

  const rangeTasks = useMemo(() => {
    if (!range) return [];
    const s = new Date(range.start), e = new Date(range.end);
    return applyFilters(allTasks.filter((t) => { const d = taskDate(t); return d && d >= s && d <= e; }));
  }, [allTasks, range, search, statusFilter, priorityFilter, assigneeFilter]);

  const activeTasks = range ? rangeTasks : monthTasks;
  const periodLabel = range ? range.label : `${MONTHS[month - 1]} ${year}`;

  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const s = { total: activeTasks.length, completed: 0, in_progress: 0, pending: 0, overdue: 0 };
    for (const t of activeTasks) {
      const st = normalizePlannerStatus(t.status);
      if (st === "completed") s.completed++;
      else if (st === "in_progress") s.in_progress++;
      else if (st === "pending" || st === "on_hold") s.pending++;
      const d = t.due_date ? String(t.due_date).slice(0, 10) : "";
      if (st !== "completed" && st !== "cancelled" && d && d < today) s.overdue++;
    }
    s.pct = s.total ? Math.round((s.completed / s.total) * 100) : 0;
    return s;
  }, [activeTasks]);

  // Inline / dialog actions — all raw-write planner endpoints.
  const quickUpdate = async (task, patch, okMsg) => {
    setAllTasks((prev) => prev.map((t) => (t.task_id === task.task_id ? { ...t, ...patch } : t)));
    try { await workPlannerApi.updateTask(task.task_id, patch); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(e.message); refetch(); }
  };
  const handleQuickStatus = (task, status) => quickUpdate(task, { status });
  const handleQuickAssign = (task, userId) => quickUpdate(task, { assigned_users: userId ? [userId] : [] });
  const handleReschedule = (task, dateStr) => quickUpdate(task, { due_date: dateStr }, `Moved to ${dateStr}`);

  const openAdd = (dateStr) => { setEditingTask(null); setDefaultDate(dateStr || `${year}-${String(month).padStart(2, "0")}-01`); setDialogOpen(true); };
  const openEdit = (task) => { setEditingTask(task); setDefaultDate(""); setDialogOpen(true); };

  const exportPdf = async () => {
    if (!pdfRef.current) return;
    setExporting(true);
    try {
      const name = (selectedClient?.client_name || selectedClient?.name || "client").replace(/\s+/g, "_");
      await html2pdf().set({
        margin: 12, filename: `Work-Planner-${name}-${periodLabel.replace(/\s+/g, "-")}.pdf`,
        image: { type: "jpeg", quality: 0.98 }, html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: "pt", format: "a4", orientation: "portrait" }, pagebreak: { mode: ["css", "legacy"] },
      }).from(pdfRef.current).save();
    } catch (e) { toast.error("PDF export failed"); }
    finally { setExporting(false); }
  };

  const pdfRows = useMemo(() => [...activeTasks].sort((a, b) => (taskDate(a)?.getTime() || 0) - (taskDate(b)?.getTime() || 0)), [activeTasks]);

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Client picker */}
      <div className="relative">
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            data-testid="planner-client-search"
            value={selectedClient && !pickerOpen ? (selectedClient.client_name || selectedClient.name) : clientSearch}
            onChange={(e) => { setClientSearch(e.target.value); setPickerOpen(true); }}
            onFocus={() => setPickerOpen(true)}
            placeholder="Choose client — search by name or company…"
            className="flex-1 bg-transparent text-sm outline-none"
          />
          {selectedClient && <button onClick={() => { navigate("/work-planner/client"); setPickerOpen(true); }} className="rounded-full p-1 hover:bg-slate-100"><X className="h-4 w-4 text-slate-400" /></button>}
        </div>
        {pickerOpen && (
          <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
            {filteredClientOptions.length === 0 ? <div className="p-3 text-sm text-slate-400">No clients found</div> :
              filteredClientOptions.map((c) => (
                <button key={c.client_id || c._id} onClick={() => { setPickerOpen(false); setClientSearch(""); navigate(`/work-planner/client/${c.client_id || c._id}?year=${year}&month=${month}`); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                  <Building2 className="h-3.5 w-3.5 text-slate-400" />{c.client_name || c.name}{c.company_name && <span className="text-slate-400">— {c.company_name}</span>}
                </button>
              ))}
          </div>
        )}
      </div>

      {!clientId ? (
        <div className="py-16 text-center text-sm text-slate-400">Choose a client above to open their monthly work planner.</div>
      ) : loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
      ) : !selectedClient ? (
        <div className="py-16 text-center text-sm text-rose-500">Client not found or you don't have access.</div>
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2">
              <button onClick={() => changeMonth(-1)} className="rounded-lg border border-slate-200 p-1.5 hover:bg-slate-50" title="Previous month"><ChevronLeft className="h-4 w-4" /></button>
              <select value={month} onChange={(e) => { setRange(null); setMonth(year, Number(e.target.value)); }} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-medium">
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <input type="number" value={year} onChange={(e) => { setRange(null); setMonth(Number(e.target.value) || year, month); }} className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
              <button onClick={() => changeMonth(1)} className="rounded-lg border border-slate-200 p-1.5 hover:bg-slate-50" title="Next month"><ChevronRight className="h-4 w-4" /></button>
              <button onClick={goToday} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium hover:bg-slate-50">Today</button>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-slate-200 p-0.5">
                {[["planner", LayoutGrid, "Planner"], ["list", List, "List"], ["calendar", CalendarDays, "Calendar"]].map(([id, Icon, label]) => (
                  <button key={id} onClick={() => { if (id !== "list") setRange(null); setViewMode(id); }} data-testid={`planner-view-${id}`}
                    className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium ${viewMode === id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                    <Icon className="h-3.5 w-3.5" />{label}
                  </button>
                ))}
              </div>
              <button onClick={exportPdf} disabled={exporting} data-testid="planner-pdf" className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
                {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Download Report PDF
              </button>
            </div>
          </div>

          {/* Client header + quick ranges */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">{selectedClient.client_name || selectedClient.name}</div>
              <div className="text-xs text-slate-500">{selectedClient.company_name ? `${selectedClient.company_name} · ` : ""}{periodLabel} · <span className="font-medium text-indigo-600">{stats.pct}% complete</span></div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {[["This Month", () => { const n = new Date(); setRange(null); setMonth(n.getFullYear(), n.getMonth() + 1); }],
                ["Last Month", () => { const d = subMonths(new Date(), 1); setRange(null); setMonth(d.getFullYear(), d.getMonth() + 1); }],
                ["Last 3 Months", () => setQuickRange(3, "Last 3 Months")],
                ["Last 6 Months", () => setQuickRange(6, "Last 6 Months")]].map(([label, fn]) => (
                <button key={label} onClick={fn} className={`rounded-full px-2.5 py-1 text-xs font-medium ${range?.label === label ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
              ))}
              {range && <button onClick={clearRange} className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-600">Clear range ✕</button>}
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {[["Total Tasks", stats.total, "text-slate-900"], ["Completed", stats.completed, "text-green-600"], ["In Progress", stats.in_progress, "text-blue-600"], ["Pending", stats.pending, "text-slate-600"], ["Overdue", stats.overdue, "text-rose-600"]].map(([label, val, cls]) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-white p-3">
                <div className={`text-2xl font-semibold ${cls}`}>{val}</div>
                <div className="text-xs text-slate-500">{label}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search task…" data-testid="planner-search" className="h-8 w-44 rounded-lg border border-slate-200 pl-8 pr-2 text-xs outline-none focus:border-indigo-400" />
            </div>
            <select value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)} className="h-8 rounded-lg border border-slate-200 px-2 text-xs">
              <option value="all">All assignees</option>
              {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-8 rounded-lg border border-slate-200 px-2 text-xs">
              <option value="all">All statuses</option>
              {PLANNER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="h-8 rounded-lg border border-slate-200 px-2 text-xs">
              <option value="all">All priorities</option>
              {PLANNER_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <button onClick={() => openAdd()} className="ml-auto inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"><Plus className="h-3.5 w-3.5" /> New Task</button>
          </div>

          {/* Views */}
          {range && viewMode !== "list" ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
              Multi-month ranges show in <button onClick={() => setViewMode("list")} className="font-medium text-indigo-600 underline">List view</button>. Planner and Calendar show one month at a time.
            </div>
          ) : viewMode === "planner" ? (
            <PlannerGrid year={year} month={month} tasks={monthTasks} members={members}
              onAddTask={openAdd} onOpenTask={openEdit} onQuickStatus={handleQuickStatus} onQuickAssign={handleQuickAssign} onReschedule={handleReschedule} />
          ) : viewMode === "calendar" ? (
            <TasksCalendarView key={`${year}-${month}`} tasks={monthTasks} priorityConfig={PRIORITY_CONFIG} initialDate={new Date(year, month - 1, 1)}
              onTaskClick={openEdit} onReschedule={(t, iso) => handleReschedule(t, iso.slice(0, 10))} onQuickCreate={(d) => openAdd(format(new Date(d), "yyyy-MM-dd"))} />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              {activeTasks.length === 0 ? <div className="p-8 text-center text-sm text-slate-400">No tasks for {periodLabel}.</div> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-slate-500"><tr>
                    <th className="p-3">Task</th><th className="p-3">Due</th><th className="p-3">Assignee</th><th className="p-3">Priority</th><th className="p-3">Status</th>
                  </tr></thead>
                  <tbody>
                    {pdfRows.map((t) => { const m = plannerStatusMeta(t.status); return (
                      <tr key={t.task_id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" onClick={() => openEdit(t)} data-testid={`planner-list-row-${t.task_id}`}>
                        <td className="p-3">{t.title}</td>
                        <td className="p-3 whitespace-nowrap">{taskDate(t) ? format(taskDate(t), "d MMM yyyy") : "—"}</td>
                        <td className="p-3">{(t.assigned_users || []).map((id) => memberName(members, id)).join(", ") || "—"}</td>
                        <td className="p-3 capitalize">{t.priority || "medium"}</td>
                        <td className="p-3"><span className={`rounded-full px-2 py-1 text-xs ring-1 ring-inset ${m.chip}`}>{m.label}</span></td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {/* Add / edit dialog */}
      <PlannerTaskDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={refetch} onDeleted={refetch}
        clientId={clientId} clientName={selectedClient?.client_name || selectedClient?.name} members={members} task={editingTask} defaultDate={defaultDate} />

      {/* Hidden printable report */}
      {selectedClient && (
        <div className="pointer-events-none absolute -left-[99999px] top-0" aria-hidden>
          <div ref={pdfRef} style={{ width: 760, padding: 24, fontFamily: "Arial, sans-serif", color: "#0f172a", background: "#fff" }}>
            <div style={{ borderBottom: "2px solid #4f46e5", paddingBottom: 10, marginBottom: 14 }}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>Client Work Planner</div>
              <div style={{ fontSize: 13, color: "#475569", marginTop: 2 }}>
                <b>Client:</b> {selectedClient.client_name || selectedClient.name}{selectedClient.company_name ? ` (${selectedClient.company_name})` : ""} &nbsp;·&nbsp; <b>Period:</b> {periodLabel}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {[["Total", stats.total], ["Completed", stats.completed], ["In Progress", stats.in_progress], ["Pending", stats.pending], ["Overdue", stats.overdue]].map(([l, v]) => (
                <div key={l} style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{v}</div><div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase" }}>{l}</div>
                </div>
              ))}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead><tr style={{ background: "#f1f5f9" }}>
                {["Date", "Task", "Assignee", "Priority", "Status"].map((h) => <th key={h} style={{ textAlign: "left", padding: "6px 8px", border: "1px solid #e2e8f0", fontSize: 10, textTransform: "uppercase", color: "#475569" }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {pdfRows.length === 0 ? <tr><td colSpan={5} style={{ padding: 16, textAlign: "center", color: "#94a3b8", border: "1px solid #e2e8f0" }}>No tasks in this period.</td></tr> :
                  pdfRows.map((t) => { const m = plannerStatusMeta(t.status); return (
                    <tr key={t.task_id}>
                      <td style={{ padding: "5px 8px", border: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{taskDate(t) ? format(taskDate(t), "EEE d-MMM") : "—"}</td>
                      <td style={{ padding: "5px 8px", border: "1px solid #e2e8f0" }}>{t.title}</td>
                      <td style={{ padding: "5px 8px", border: "1px solid #e2e8f0" }}>{(t.assigned_users || []).map((id) => memberName(members, id)).join(", ") || "—"}</td>
                      <td style={{ padding: "5px 8px", border: "1px solid #e2e8f0", textTransform: "capitalize" }}>{t.priority || "medium"}</td>
                      <td style={{ padding: "5px 8px", border: "1px solid #e2e8f0" }}>{m.label}</td>
                    </tr>
                  ); })}
              </tbody>
            </table>
            <div style={{ marginTop: 14, fontSize: 9, color: "#94a3b8" }}>Generated {format(new Date(), "d MMM yyyy, h:mm a")} · Dnexus CRM</div>
          </div>
        </div>
      )}
    </div>
  );
}
